import { AcknowledgmentResponseStatus, ErrorCode } from '../../../services/socket/SocketEvents.tsx';
import type { Block } from '../GridDndEditor/models/Block.model';
import { addBlockViaSocket } from '../grid/reduxStore/saveHandlers.ts';
import { addGridBlockState } from '../grid/reduxStore/editorSlice';
import { setSaveStatus, setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus';
import type { GridBlockType } from 'services/entities/BlockTypes';
import type { TableType } from '../grid/reduxStore/table.types';
import type { BlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import { deleteBlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import { addBlockSettings } from '../grid/reduxStore/blockStyleSettingsSlice.ts';
import { deleteNewBlockWithSocket } from '../grid/reduxStore/gridBlockSocketActions.ts';
import type { BlockCommandDependencies } from './BlockBaseCommand.ts';
import { BlockBaseCommand } from './BlockBaseCommand.ts';
import type { SignatureBox } from 'services/entities/SignatureTypes';

export interface CreateBlockData {
  htmlContent?: string;
  content?: TableType;
  styles?: BlockSettings;
}

export class CreateBlockCommand extends BlockBaseCommand {
  private readonly originalBlockId: string;
  private currentBlockId: string;
  private readonly sectionId: string;
  private readonly blockType: GridBlockType;
  private readonly blockConfig: Block;
  private readonly blockData: CreateBlockData;
  private readonly getSignaturesBySectionId?: (sectionId: string) => SignatureBox[];
  private lastErrorCode: ErrorCode = ErrorCode.LOCKED;

  constructor(
    sectionId: string,
    blockId: string,
    blockConfig: Block,
    blockType: GridBlockType,
    blockData: CreateBlockData,
    dependencies: BlockCommandDependencies & {
      getSignaturesBySectionId?: (sectionId: string) => SignatureBox[];
    }
  ) {
    super(dependencies);
    this.originalBlockId = blockId;
    this.currentBlockId = blockId;
    this.sectionId = sectionId;
    this.blockType = blockType;
    this.blockConfig = blockConfig;
    this.blockData = structuredClone(blockData);
    this.getSignaturesBySectionId = dependencies.getSignaturesBySectionId;
  }

  async undo(): Promise<boolean> {
    try {
      this.setSaving();
      this.dispatch(deleteBlockSettings({ blockId: this.currentBlockId }));

      const response = await this.dispatch(
        deleteNewBlockWithSocket({
          blockId: this.currentBlockId,
          socketClient: this.socketClient,
          userId: this.userId,
          callback: ({ status, errorCode }) => {
            this.handleDeleteSuccess(this.sectionId, this.currentBlockId, status, errorCode, this.getSignaturesBySectionId);
          },
        })
      ).unwrap();

      return response.status === AcknowledgmentResponseStatus.OK;
    } catch (error) {
      console.error('Failed to undo block creation:', error);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  async redo(): Promise<boolean> {
    try {
      this.setSaving();

      const response = await this.persistBlockViaSocket();

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.setNotSaved();
        return false;
      }

      const responseBlockId = response.content?.blockId;
      if (!responseBlockId) {
        this.setNotSaved();
        return false;
      }

      this.applyBlockToState(responseBlockId);

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          errorCode: response.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('Failed to redo block creation:', error);
      const message = error instanceof Error ? error.message : null;
      this.lastErrorCode = message === ErrorCode.VALIDATION_ERROR || message === ErrorCode.INTERNAL_ERROR ? message : ErrorCode.LOCKED;
      this.checkDocumentLockStatus(this.lastErrorCode);
      this.setNotSaved();
      return false;
    }
  }

  redoWithoutPersistingSocket(blockId: string = this.originalBlockId): boolean {
    try {
      this.applyBlockToState(blockId);
      return true;
    } catch (error) {
      return false;
    }
  }

  private async persistBlockViaSocket() {
    const blockSnapshot = {
      type: this.blockType,
      content: this.blockData.content ?? this.blockData.htmlContent,
      contentTable: this.blockData.content,
      blockConfig: this.blockConfig,
      styles: this.blockData.styles,
    };

    const payload = this.buildBlockPayloadForSocket(blockSnapshot, this.originalBlockId, this.sectionId);
    return addBlockViaSocket(this.socketClient, payload);
  }

  private applyBlockToState(blockId: string): void {
    this.currentBlockId = blockId;

    this.dispatch(
      addGridBlockState({
        sectionId: this.sectionId,
        blockId: this.currentBlockId,
        content: this.blockData.content || this.blockData.htmlContent || '',
        blockConfig: {
          ...this.blockConfig,
          id: this.currentBlockId,
        },
        blockType: this.blockType,
      })
    );

    if (this.blockData.styles) {
      this.dispatch(
        addBlockSettings({
          blockId: this.currentBlockId,
          blockSettings: this.blockData.styles,
        })
      );
    }

    this.recalculatePageHeight(this.sectionId, this.getSignaturesBySectionId);
  }

  getCurrentBlockId(): string {
    return this.currentBlockId;
  }

  getLastErrorCode(): ErrorCode {
    return this.lastErrorCode;
  }
}
